"use client";

import Image from "next/image";
import Link from "next/link";
import { useMemo, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Minus,
  Plus,
  ShoppingBag,
} from "lucide-react";

import Footer from "@/components/footer";
import Navbar from "@/components/navbar";
import { DONGL_PRELAUNCH } from "@/components/store/dongl-config";
import type { StoreProduct, StoreVariant } from "@/components/store/types";
import { useCart } from "@/context/cart-context";

type DonglProductDetailProps = Readonly<{
  product: StoreProduct;
}>;

const MAX_QUANTITY = 10;
const DONGL_PAGE_URL =
  "https://simpleble.org/dongl?utm_source=simpleble_store&utm_medium=store&utm_campaign=dongl_product";

const getVariantLabel = (variant: StoreVariant): string => {
  return (
    [variant.colorName, variant.sizeName].filter(Boolean).join(" / ") ||
    "Standard"
  );
};

const DonglPlaceholder = (): React.ReactElement => {
  return (
    <div className="relative flex h-full min-h-[360px] w-full items-center justify-center overflow-hidden bg-[#061528] sm:min-h-[520px]">
      <div className="absolute inset-0 [background-image:linear-gradient(rgba(80,165,255,.22)_1px,transparent_1px),linear-gradient(90deg,rgba(80,165,255,.22)_1px,transparent_1px)] [background-size:34px_34px] opacity-30" />
      <div className="relative flex w-[72%] max-w-sm -rotate-3 flex-col rounded-[2rem] border border-white/15 bg-white/[0.07] p-8 shadow-2xl backdrop-blur-sm sm:p-10">
        <Image
          src="/images/logos/logomark.png"
          alt=""
          width={70}
          height={70}
          className="h-14 w-14 rounded-xl"
        />
        <div className="mt-16 h-2 w-full rounded-full bg-white/10">
          <div className="bg-primary h-full w-2/3 rounded-full" />
        </div>
      </div>
    </div>
  );
};

export const DonglProductDetail = ({
  product,
}: DonglProductDetailProps): React.ReactElement => {
  const { addToCart } = useCart();
  const firstAvailableVariant = useMemo(
    () =>
      product.variants.find((variant) => variant.isAvailable) ??
      product.variants[0],
    [product],
  );
  const [selectedVariantId, setSelectedVariantId] = useState(
    firstAvailableVariant?.id ?? "",
  );
  const [quantity, setQuantity] = useState(1);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const selectedVariant =
    product.variants.find((variant) => variant.id === selectedVariantId) ??
    firstAvailableVariant;
  const selectedColor = selectedVariant?.colorName;
  const colorImages = selectedColor
    ? product.imagesByColor[selectedColor]
    : undefined;
  const images =
    colorImages && colorImages.length > 0 ? colorImages : product.images;
  const activeImage = images[activeImageIndex] ?? images[0];
  const canAddToCart = Boolean(
    selectedVariant?.isAvailable &&
    !product.isSoldOut &&
    product.variants.length > 0,
  );

  const handleAddToCart = (): void => {
    if (!selectedVariant || !canAddToCart) {
      return;
    }

    addToCart(product, { variant: selectedVariant, quantity });
  };

  return (
    <>
      <Navbar variant="light" />
      <main className="text-foreground bg-white pt-24 sm:pt-28">
        <div className="mx-auto max-w-[1312px] px-4 md:px-16">
          <div className="flex items-center justify-between py-6">
            <Link
              href="/dongl"
              className="font-sora text-secondary hover:text-primary flex items-center gap-2 text-sm font-semibold transition-colors"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Back to Dongl
            </Link>
            <nav
              className="text-secondary hidden items-center gap-2 text-sm font-semibold sm:flex"
              aria-label="Breadcrumb"
            >
              <Link href="/" className="hover:text-primary transition-colors">
                Store
              </Link>
              <span aria-hidden="true">/</span>
              <span className="text-foreground">{product.name}</span>
            </nav>
          </div>

          <section className="grid min-w-0 gap-8 pb-16 sm:pb-24 lg:grid-cols-[1.08fr_0.92fr] lg:items-start lg:gap-12">
            <div className="flex min-w-0 flex-col-reverse gap-4 lg:flex-row">
              {images.length > 1 && (
                <div className="flex scrollbar-thin gap-3 overflow-x-auto pb-1 lg:max-h-[620px] lg:flex-col lg:overflow-y-auto lg:pr-1">
                  {images.map((image, index) => (
                    <button
                      key={image.id}
                      type="button"
                      onClick={() => setActiveImageIndex(index)}
                      aria-label={`Show image ${index + 1} of ${images.length}`}
                      className={`relative aspect-square w-20 shrink-0 cursor-pointer overflow-hidden rounded-xl border bg-[#eef6ff] transition-all ${
                        activeImageIndex === index
                          ? "border-primary ring-primary/20 ring-2"
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      <Image
                        src={image.transformedUrl ?? image.url}
                        alt={`${product.name} thumbnail ${index + 1}`}
                        fill
                        sizes="80px"
                        className="object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}

              <div className="border-border relative aspect-square w-full min-w-0 flex-1 overflow-hidden rounded-2xl border bg-[#eef6ff] md:rounded-3xl">
                {activeImage ? (
                  <Image
                    src={activeImage.transformedUrl ?? activeImage.url}
                    alt={product.name}
                    fill
                    priority
                    sizes="(max-width: 1024px) 100vw, 52vw"
                    className="object-cover"
                  />
                ) : (
                  <DonglPlaceholder />
                )}
                {product.isSoldOut && (
                  <span className="font-sora bg-foreground/85 absolute top-6 left-6 rounded-full px-4 py-2 text-sm font-semibold text-white">
                    {DONGL_PRELAUNCH ? "Coming soon" : "Sold out"}
                  </span>
                )}
              </div>
            </div>

            <div className="border-border flex min-w-0 flex-col rounded-2xl border bg-white p-5 shadow-[0_24px_70px_rgba(23,43,77,0.08)] sm:p-7 md:rounded-3xl lg:p-9">
              <h1 className="font-sora text-foreground text-2xl leading-[1.1] font-semibold tracking-[-0.03em] sm:text-3xl md:text-4xl">
                {product.name}
              </h1>
              {product.description && (
                <p className="text-secondary mt-5 text-base leading-7 sm:text-lg">
                  {product.description}
                </p>
              )}

              <div className="border-border mt-7 border-y py-6">
                <p className="font-sora text-primary text-2xl font-semibold">
                  {selectedVariant?.priceLabel ??
                    product.priceLabel ??
                    "Pricing unavailable"}
                  {selectedVariant?.compareAtPriceLabel && (
                    <s className="text-secondary ml-2.5 text-base font-medium">
                      {selectedVariant.compareAtPriceLabel}
                    </s>
                  )}
                </p>
                <p className="mt-4 flex items-center gap-2 text-sm font-semibold">
                  <span
                    className={`h-2.5 w-2.5 rounded-full ${
                      canAddToCart ? "bg-emerald-500" : "bg-amber-400"
                    }`}
                    aria-hidden="true"
                  />
                  {product.isSoldOut
                    ? DONGL_PRELAUNCH
                      ? "Coming soon"
                      : "Currently sold out"
                    : "Available to order"}
                </p>
              </div>

              {product.variants.length > 1 && (
                <div className="mt-6">
                  <p className="font-sora mb-3 text-sm font-semibold">
                    Configuration
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {product.variants.map((variant) => {
                      const isSelected = variant.id === selectedVariant?.id;

                      return (
                        <button
                          key={variant.id}
                          type="button"
                          disabled={!variant.isAvailable}
                          onClick={() => {
                            setSelectedVariantId(variant.id);
                            setActiveImageIndex(0);
                          }}
                          aria-pressed={isSelected}
                          className={`font-sora min-h-11 cursor-pointer rounded-xl border px-4 text-sm font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-40 ${
                            isSelected
                              ? "border-primary text-primary ring-primary/10 ring-2"
                              : "border-border hover:border-primary/50"
                          }`}
                        >
                          {isSelected && (
                            <Check className="mr-2 inline h-3.5 w-3.5" />
                          )}
                          {getVariantLabel(variant)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="mt-6">
                <p className="font-sora mb-3 text-sm font-semibold">Quantity</p>
                <div className="border-border inline-flex min-h-11 items-center rounded-xl border bg-white">
                  <button
                    type="button"
                    onClick={() =>
                      setQuantity((current) => Math.max(1, current - 1))
                    }
                    disabled={quantity === 1}
                    className="hover:text-primary flex h-11 w-11 cursor-pointer items-center justify-center disabled:cursor-not-allowed disabled:opacity-35"
                    aria-label="Decrease quantity"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="font-sora min-w-10 text-center text-sm font-semibold">
                    {quantity}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setQuantity((current) =>
                        Math.min(MAX_QUANTITY, current + 1),
                      )
                    }
                    disabled={quantity === MAX_QUANTITY}
                    className="hover:text-primary flex h-11 w-11 cursor-pointer items-center justify-center disabled:cursor-not-allowed disabled:opacity-35"
                    aria-label="Increase quantity"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="mt-7 grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  disabled={!canAddToCart}
                  onClick={handleAddToCart}
                  className={`font-sora inline-flex min-h-14 items-center justify-center gap-2 rounded-2xl px-5 text-sm font-semibold transition-colors ${
                    canAddToCart
                      ? "bg-primary cursor-pointer text-white shadow-md hover:bg-[#176fd5]"
                      : "bg-muted text-muted-foreground cursor-not-allowed"
                  }`}
                >
                  <ShoppingBag className="h-5 w-5" />
                  {product.isSoldOut
                    ? DONGL_PRELAUNCH
                      ? "Coming soon"
                      : "Sold out"
                    : "Add to cart"}
                </button>
                <Link
                  href={DONGL_PAGE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-sora border-primary text-primary hover:bg-primary/5 inline-flex min-h-14 items-center justify-center gap-2 rounded-2xl border px-5 text-sm font-semibold transition-colors"
                >
                  About Dongl
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
};
